// HeroBuilding — exploded isometric 5-floor patrol-tower visual for the
// SENTRY NEXUS landing hero (v4 — asymmetric "peek-down" layout).
//
// v4 layout shift over v3:
//
//   F5  ┐ tight   ──┐
//   F4  ┘            │ "lid" — peeled off the top of the building
//                    │
//        ↕ BIG GAP ─┘  (the only big gap, the "peek")
//
//   F3 (focus, 120 %, FRONT)    ┐
//   F2  tight                    │ "base" — 3 floors stacked compact
//   F1  tight                    ┘
//
// F3 sits visually on top of F1/F2 (rendered LAST), with floor-plan
// detail + LIVE chip + 「即時警示定位」callout all anchored on F3's
// right-side gutter so they don't crowd into the lid gap above.
//
// Geometry (viewBox 800×850):
//
//   F5 cy=110  w=420  ┐ tight pair (cy_diff 105)
//   F4 cy=215  w=420  ┘
//                     ↕ BIG GAP (cy_diff 255)
//   F3 cy=470  w=504    (focus, 120 %, drawn LAST so it pops above
//                       F1/F2 even though F2 starts inside its slab.)
//   F2 cy=575  w=420    (tight cy_diff 105, base middle)
//   F1 cy=680  w=420    (tight cy_diff 105, base bottom)
//
// The cy_diff 105 between F3 and F2 means F2's top vertex lands at
// F3's center — F2's slab visually sits inside F3's south half. We
// render F3 LAST so its top face + floor plan stay visible; F3 reads
// as a wide overhang on top of the F1+F2 base.
//
// Other v3 features carried over: distinct floor plans, zh-TW labels,
// silhouette, patrol path, prefers-reduced-motion fallback, mobile
// responsive CSS.

import React from "react";
import styles from "./HeroBuilding.module.css";

type PlanKind = "lobby" | "office" | "location" | "meeting" | "exec";

type Floor = {
  cx: number;
  cy: number;
  w: number;
  thickness?: number;
  plan: PlanKind;
  label: string;
  level: 1 | 2 | 3 | 4 | 5;
};

const VIEW_W = 800;
const VIEW_H = 850;
const THICKNESS = 16;

const FLOORS: Floor[] = [
  { cx: 400, cy: 110, w: 420, plan: "exec",       level: 5, label: "高管" },
  { cx: 400, cy: 215, w: 420, plan: "meeting",    level: 4, label: "會議" },
  { cx: 400, cy: 470, w: 504, plan: "location", level: 3, label: "中控" },
  { cx: 400, cy: 575, w: 420, plan: "office",     level: 2, label: "辦公" },
  { cx: 400, cy: 680, w: 420, plan: "lobby",      level: 1, label: "大廳" },
];

const FOCUS_INDEX = 2;

const C_FOCUS = "#00D4FF";
const C_OTHER = "#4FC3F7";

function rhombusVerts(f: Floor) {
  const dx = f.w / 2;
  const dy = f.w / 4;
  return {
    n: [f.cx, f.cy - dy] as const,
    e: [f.cx + dx, f.cy] as const,
    s: [f.cx, f.cy + dy] as const,
    w: [f.cx - dx, f.cy] as const,
  };
}

function topPath(f: Floor): string {
  const v = rhombusVerts(f);
  return `M ${v.n[0]},${v.n[1]} L ${v.e[0]},${v.e[1]} L ${v.s[0]},${v.s[1]} L ${v.w[0]},${v.w[1]} Z`;
}

function sideRightPath(f: Floor): string {
  const v = rhombusVerts(f);
  const t = f.thickness ?? THICKNESS;
  return `M ${v.e[0]},${v.e[1]} L ${v.s[0]},${v.s[1]} L ${v.s[0]},${v.s[1] + t} L ${v.e[0]},${v.e[1] + t} Z`;
}

function sideLeftPath(f: Floor): string {
  const v = rhombusVerts(f);
  const t = f.thickness ?? THICKNESS;
  return `M ${v.w[0]},${v.w[1]} L ${v.s[0]},${v.s[1]} L ${v.s[0]},${v.s[1] + t} L ${v.w[0]},${v.w[1] + t} Z`;
}

function placeOnFloor(f: Floor, u: number, v: number): [number, number] {
  return [
    f.cx + (u - v) * (f.w / 2),
    f.cy + (u + v - 1) * (f.w / 4),
  ];
}

export interface HeroBuildingProps {
  className?: string;
}

export default function HeroBuilding({ className }: HeroBuildingProps): React.ReactElement {
  const focus = FLOORS[FOCUS_INDEX]!;

  // Patrol indicator positions on F3.
  const greenA = placeOnFloor(focus, 0.30, 0.45);
  const greenB = placeOnFloor(focus, 0.72, 0.40);
  const red    = placeOnFloor(focus, 0.50, 0.78);

  // Patrol path — two quadratic segments with explicit controls inside
  // F3 so the curve never escapes the floor's interior (and therefore
  // never spills above into the F4 gap or below into F2's top).
  const ctrl1  = placeOnFloor(focus, 0.55, 0.38);
  const ctrl2  = placeOnFloor(focus, 0.65, 0.62);
  const pathD  = `M ${greenA[0]},${greenA[1]} Q ${ctrl1[0]},${ctrl1[1]} ${greenB[0]},${greenB[1]} Q ${ctrl2[0]},${ctrl2[1]} ${red[0]},${red[1]}`;

  // East-side callout — sits in the right-hand gutter outside F3 so it
  // doesn't crowd the F4 gap above or the F2 stack below.
  const focusE = rhombusVerts(focus).e; // (cx+w/2, cy) = (652, 470)
  // Arrow tail (near the text) and head (just east of the red dot).
  const calloutTextX = focusE[0] + 12;
  const calloutTextY = focusE[1] + 60;     // ≈ (664, 530)
  const calloutTailX = focusE[0] + 6;
  const calloutTailY = focusE[1] + 48;
  const calloutHeadX = red[0] + 14;
  const calloutHeadY = red[1] - 2;

  // LIVE chip — also east-side; right outside the focus floor's east
  // vertex, mid-height, above the callout.
  const liveChipX = focusE[0] + 8;
  const liveChipY = focusE[1] - 8;

  // Non-focus floors split into "lid" (above F3) and "base" (below F3)
  // so we can render F3 last on top of base.
  const lidFloors  = FLOORS.slice(0, FOCUS_INDEX);                 // [F5, F4]
  const baseFloors = FLOORS.slice(FOCUS_INDEX + 1);                // [F2, F1]

  return (
    <svg
      viewBox={`0 0 ${VIEW_W} ${VIEW_H}`}
      xmlns="http://www.w3.org/2000/svg"
      className={`${styles.root} ${className ?? ""}`.trim()}
      role="img"
      aria-label="SENTRY NEXUS — exploded view: a base of 3 floors with the third (中控) on top, and 2 floors lifted off as a peeled lid"
    >
      <defs>
        {/* Focus-floor outer glow — stronger in v4 (stdDeviation 7 → 8.5). */}
        <filter id="hb-glow" x="-30%" y="-30%" width="160%" height="160%">
          <feGaussianBlur stdDeviation="8.5" result="blur" />
          <feMerge>
            <feMergeNode in="blur" />
            <feMergeNode in="SourceGraphic" />
          </feMerge>
        </filter>
        <linearGradient id="hb-top-fill" x1="0%" y1="0%" x2="0%" y2="100%">
          <stop offset="0%"  stopColor="rgba(79, 195, 247, 0.22)" />
          <stop offset="100%" stopColor="rgba(40, 130, 200, 0.08)" />
        </linearGradient>
        <linearGradient id="hb-side-right" x1="0%" y1="0%" x2="0%" y2="100%">
          <stop offset="0%"  stopColor="rgba(79, 195, 247, 0.20)" />
          <stop offset="100%" stopColor="rgba(0, 90, 150, 0.08)" />
        </linearGradient>
        <linearGradient id="hb-side-left" x1="0%" y1="0%" x2="0%" y2="100%">
          <stop offset="0%"  stopColor="rgba(40, 150, 200, 0.18)" />
          <stop offset="100%" stopColor="rgba(0, 70, 120, 0.06)" />
        </linearGradient>
        <linearGradient id="hb-top-focus" x1="0%" y1="0%" x2="0%" y2="100%">
          <stop offset="0%"  stopColor="rgba(0, 230, 255, 0.40)" />
          <stop offset="100%" stopColor="rgba(0, 160, 220, 0.18)" />
        </linearGradient>
        <marker
          id="hb-arrow"
          viewBox="0 0 10 10"
          refX="8" refY="5"
          markerWidth="6" markerHeight="6"
          orient="auto-start-reverse"
        >
          <path d="M 0,0 L 10,5 L 0,10 z" fill={C_FOCUS} />
        </marker>
      </defs>

      {/* Background atmosphere — soft cyan radial. */}
      <ellipse cx={VIEW_W / 2} cy={VIEW_H / 2} rx={340} ry={400}
        fill="rgba(0, 212, 255, 0.05)" />

      {/* Building silhouette — dashed outline, sized to v4's tighter
          envelope. */}
      <BuildingSilhouette />

      {/* Single big-gap flow line: F4 → F3 (the only large gap in v4). */}
      <FlowLine from={FLOORS[1]!} to={FLOORS[2]!} />

      {/* Lid floors — F5 then F4 (back-to-front within the lid pair). */}
      {lidFloors.map((f, i) => (
        <FloorGroup key={`lid-${i}`} f={f} focus={false} />
      ))}

      {/* Base floors — F2 then F1 (back-to-front for the base stack). */}
      {baseFloors.map((f, i) => (
        <FloorGroup key={`base-${i}`} f={f} focus={false} />
      ))}

      {/* F3 LAST — focus floor renders on top of the base + lid so its
          interior + label stay visible despite F2 overlapping its slab. */}
      <FloorGroup f={focus} focus />

      {/* F3 patrol path — drawn AFTER F3 so it sits above the floor plan
          walls. (Path geometry is clamped inside F3.) */}
      <path
        d={pathD}
        fill="none"
        stroke="rgba(0, 212, 255, 0.65)"
        strokeWidth={1.0}
        strokeDasharray="4 4"
        className={styles.patrolPath}
      />

      {/* Patrol indicators. */}
      <g className={styles.patrolLayer}>
        <PatrolDot x={greenA[0]} y={greenA[1]} variant="green" />
        <PatrolDot x={greenB[0]} y={greenB[1]} variant="green" />
        <PatrolDot x={red[0]}    y={red[1]}    variant="red"   />
        {/* Outward radar pulse on the red dot. */}
        <circle
          cx={red[0]} cy={red[1]} r={24}
          fill="none"
          stroke="rgba(255, 77, 109, 0.6)"
          strokeWidth={1.4}
          className={styles.pulseRing}
        />
      </g>

      {/* "即時警示定位" callout — east-side gutter, points west into red dot. */}
      <g className={styles.alertCallout}>
        <line
          x1={calloutTailX} y1={calloutTailY}
          x2={calloutHeadX} y2={calloutHeadY}
          stroke={C_FOCUS}
          strokeWidth={1.2}
          markerEnd="url(#hb-arrow)"
        />
        <text
          x={calloutTextX} y={calloutTextY}
          fill={C_FOCUS}
          fontSize={11}
          fontWeight={500}
          className={styles.alertCalloutText}
        >
          即時警示定位
        </text>
      </g>

      {/* LIVE chip — east-side gutter, just outside F3's east vertex,
          drawn last so it stays above any overlap. */}
      <LiveChip x={liveChipX} y={liveChipY} />
    </svg>
  );
}

// ── Floor group (slab + interior + label) ────────────────────────────────

function FloorGroup({ f, focus }: { f: Floor; focus: boolean }): React.ReactElement {
  const c = focus ? C_FOCUS : C_OTHER;
  return (
    <g
      className={styles.floor}
      filter={focus ? "url(#hb-glow)" : undefined}
    >
      <path
        d={sideLeftPath(f)}
        fill="url(#hb-side-left)"
        stroke={`${c}88`}
        strokeWidth={1}
        strokeLinejoin="round"
      />
      <path
        d={sideRightPath(f)}
        fill="url(#hb-side-right)"
        stroke={`${c}88`}
        strokeWidth={1}
        strokeLinejoin="round"
      />
      <path
        d={topPath(f)}
        fill={focus ? "url(#hb-top-focus)" : "url(#hb-top-fill)"}
        stroke={focus ? C_FOCUS : `${C_OTHER}aa`}
        strokeWidth={focus ? 1.8 : 1.2}
        strokeLinejoin="round"
      />
      {/* v4 — focus floor gets a 1 px white halo stroke (offset 0,
          opacity 0.4) on top of the cyan to make the edge feel thicker
          than the surrounding floors. */}
      {focus && (
        <>
          <path
            d={topPath(f)}
            fill="none"
            stroke="rgba(255, 255, 255, 0.40)"
            strokeWidth={1}
          />
          <path
            d={sideLeftPath(f)}
            fill="none"
            stroke="rgba(255, 255, 255, 0.30)"
            strokeWidth={1}
          />
          <path
            d={sideRightPath(f)}
            fill="none"
            stroke="rgba(255, 255, 255, 0.30)"
            strokeWidth={1}
          />
        </>
      )}
      <FloorPlan floor={f} kind={f.plan} focus={focus} />
      <FloorLabel floor={f} focus={focus} />
    </g>
  );
}

// ── Flow line between two floors (used only for the F4↔F3 big gap) ───────

function FlowLine({ from, to }: { from: Floor; to: Floor }): React.ReactElement {
  const top = rhombusVerts(from);
  const bot = rhombusVerts(to);
  const t = from.thickness ?? THICKNESS;
  return (
    <g className={styles.flowLine}>
      <line
        x1={top.s[0]} y1={top.s[1] + t}
        x2={bot.n[0]} y2={bot.n[1]}
        stroke="rgba(0, 212, 255, 0.45)"
        strokeWidth={1.2}
        strokeDasharray="3 5"
      />
      <line
        x1={top.w[0] + (top.s[0] - top.w[0]) * 0.5}
        y1={top.w[1] + t + 4}
        x2={bot.w[0] + (bot.s[0] - bot.w[0]) * 0.5}
        y2={bot.w[1] - 4}
        stroke="rgba(0, 212, 255, 0.22)"
        strokeWidth={1}
        strokeDasharray="2 6"
      />
      <line
        x1={top.e[0] + (top.s[0] - top.e[0]) * 0.5}
        y1={top.e[1] + t + 4}
        x2={bot.e[0] + (bot.s[0] - bot.e[0]) * 0.5}
        y2={bot.e[1] - 4}
        stroke="rgba(0, 212, 255, 0.22)"
        strokeWidth={1}
        strokeDasharray="2 6"
      />
    </g>
  );
}

// ── Building silhouette ──────────────────────────────────────────────────

function BuildingSilhouette(): React.ReactElement {
  const w = 460;
  const dx = w / 2;
  const dy = w / 4;
  const cx = 400;
  const top_cy = 80;
  const bot_cy = 740;

  const N_top: [number, number] = [cx,        top_cy - dy];
  const E_top: [number, number] = [cx + dx,   top_cy];
  const E_bot: [number, number] = [cx + dx,   bot_cy];
  const S_bot: [number, number] = [cx,        bot_cy + dy];
  const W_bot: [number, number] = [cx - dx,   bot_cy];
  const W_top: [number, number] = [cx - dx,   top_cy];
  const d = `M ${N_top.join(",")} L ${E_top.join(",")} L ${E_bot.join(",")} L ${S_bot.join(",")} L ${W_bot.join(",")} L ${W_top.join(",")} Z`;
  return (
    <g className={styles.silhouette}>
      <path
        d={d}
        fill="none"
        stroke="rgba(0, 212, 255, 0.20)"
        strokeWidth={1.0}
        strokeDasharray="4 6"
      />
      <path
        d={`M ${N_top.join(",")} L ${E_top.join(",")} L ${cx},${top_cy + dy} L ${W_top.join(",")} Z`}
        fill="none"
        stroke="rgba(0, 212, 255, 0.12)"
        strokeWidth={0.8}
        strokeDasharray="3 6"
      />
    </g>
  );
}

// ── Floor label ──────────────────────────────────────────────────────────

function FloorLabel({ floor, focus }: { floor: Floor; focus: boolean }): React.ReactElement {
  const [x, y] = placeOnFloor(floor, -0.08, 1.04);
  const text = `F${floor.level} ${floor.label}`;

  if (focus) {
    const padX = 6;
    const padY = 3;
    const textW = 56;
    const textH = 14;
    return (
      <g>
        <rect
          x={x - padX}
          y={y - textH + 2}
          width={textW + padX * 2}
          height={textH + padY * 2}
          rx={3}
          fill="rgba(0, 212, 255, 0.20)"
          stroke="rgba(0, 212, 255, 0.6)"
          strokeWidth={0.8}
        />
        <text
          x={x}
          y={y}
          fill={C_FOCUS}
          fontSize={11}
          fontWeight={600}
          letterSpacing="0.04em"
          className={styles.label}
        >
          {text}
        </text>
      </g>
    );
  }

  return (
    <text
      x={x}
      y={y}
      fill="#6FE5FF"
      fillOpacity={0.7}
      fontSize={10}
      fontWeight={500}
      letterSpacing="0.04em"
      className={styles.label}
    >
      {text}
    </text>
  );
}

// ── LIVE chip ────────────────────────────────────────────────────────────

function LiveChip({ x, y }: { x: number; y: number }): React.ReactElement {
  const w = 38;
  const h = 16;
  return (
    <g className={styles.liveChip}>
      <rect
        x={x} y={y}
        width={w} height={h}
        rx={3}
        fill="#ff4d6d"
      />
      <circle cx={x + 7} cy={y + h / 2} r={2.4} fill="#fff" className={styles.liveDot} />
      <text
        x={x + 14}
        y={y + h - 4}
        fill="#fff"
        fontSize={9}
        fontWeight={700}
        letterSpacing="0.08em"
      >
        LIVE
      </text>
    </g>
  );
}

// ── Floor plan layer ─────────────────────────────────────────────────────

interface FloorPlanProps {
  floor: Floor;
  kind: PlanKind;
  focus?: boolean;
}

function FloorPlan({ floor, kind, focus }: FloorPlanProps): React.ReactElement {
  const stroke = focus ? `${C_FOCUS}cc` : `${C_OTHER}99`;
  const strokeStrong = focus ? C_FOCUS : `${C_OTHER}cc`;
  const fillSubtle = focus ? "rgba(0, 220, 255, 0.10)" : "rgba(79, 195, 247, 0.07)";
  const sw = focus ? 1.0 : 0.7;

  const Rect = (
    u1: number, v1: number, u2: number, v2: number,
    opts: { fill?: string; stroke?: string; sw?: number; dashed?: boolean; key?: string } = {},
  ) => {
    const c = [
      [u1, v1], [u2, v1], [u2, v2], [u1, v2],
    ].map(([u, v]) => placeOnFloor(floor, u, v));
    const d = `M ${c[0]![0]},${c[0]![1]} L ${c[1]![0]},${c[1]![1]} L ${c[2]![0]},${c[2]![1]} L ${c[3]![0]},${c[3]![1]} Z`;
    return (
      <path
        key={opts.key ?? `r-${u1}-${v1}-${u2}-${v2}`}
        d={d}
        fill={opts.fill ?? "none"}
        stroke={opts.stroke ?? stroke}
        strokeWidth={opts.sw ?? sw}
        strokeLinejoin="miter"
        strokeDasharray={opts.dashed ? "3 3" : undefined}
      />
    );
  };
  const Seg = (
    u1: number, v1: number, u2: number, v2: number,
    opts: { stroke?: string; sw?: number; dashed?: boolean; key?: string } = {},
  ) => {
    const [x1, y1] = placeOnFloor(floor, u1, v1);
    const [x2, y2] = placeOnFloor(floor, u2, v2);
    return (
      <line
        key={opts.key ?? `s-${u1}-${v1}-${u2}-${v2}`}
        x1={x1} y1={y1} x2={x2} y2={y2}
        stroke={opts.stroke ?? stroke}
        strokeWidth={opts.sw ?? sw}
        strokeDasharray={opts.dashed ? "3 3" : undefined}
      />
    );
  };
  const Dot = (u: number, v: number, r: number, k: string, fill?: string) => {
    const [cx, cy] = placeOnFloor(floor, u, v);
    return <circle key={k} cx={cx} cy={cy} r={r} fill={fill ?? stroke} />;
  };
  const Curve = (
    u1: number, v1: number, cu: number, cv: number, u2: number, v2: number,
    opts: { stroke?: string; sw?: number; key?: string } = {},
  ) => {
    const [x1, y1] = placeOnFloor(floor, u1, v1);
    const [cx, cy] = placeOnFloor(floor, cu, cv);
    const [x2, y2] = placeOnFloor(floor, u2, v2);
    return (
      <path
        key={opts.key ?? `c-${u1}-${v1}-${u2}-${v2}`}
        d={`M ${x1},${y1} Q ${cx},${cy} ${x2},${y2}`}
        fill="none"
        stroke={opts.stroke ?? stroke}
        strokeWidth={opts.sw ?? sw}
      />
    );
  };

  const PERIM = { u1: 0.10, v1: 0.10, u2: 0.90, v2: 0.90 } as const;

  switch (kind) {
    case "lobby":
      return (
        <g>
          {Rect(PERIM.u1, PERIM.v1, PERIM.u2, PERIM.v2, { key: "perim" })}
          {(() => {
            const [cx, cy] = placeOnFloor(floor, 0.42, 0.50);
            return (
              <ellipse
                key="reception"
                cx={cx} cy={cy}
                rx={32} ry={16}
                fill={fillSubtle}
                stroke={strokeStrong}
                strokeWidth={sw + 0.1}
              />
            );
          })()}
          {(() => {
            const [cx, cy] = placeOnFloor(floor, 0.42, 0.50);
            return (
              <ellipse
                key="reception-inner"
                cx={cx} cy={cy}
                rx={20} ry={10}
                fill="none"
                stroke={stroke}
                strokeWidth={sw - 0.1}
                strokeDasharray="2 3"
              />
            );
          })()}
          {Rect(0.65, 0.55, 0.85, 0.82, { fill: fillSubtle, key: "guard" })}
          {Dot(0.75, 0.68, 1.6, "guard-desk")}
          {Curve(0.18, 0.62, 0.27, 0.66, 0.36, 0.62, { sw: sw + 0.2, key: "sofa-1" })}
          {Curve(0.20, 0.66, 0.27, 0.72, 0.34, 0.66, { sw: sw, key: "sofa-2" })}
          {Seg(0.42, 0.86, 0.42, 0.92, { sw: sw + 0.2, key: "door-l" })}
          {Seg(0.55, 0.86, 0.55, 0.92, { sw: sw + 0.2, key: "door-r" })}
          {Seg(0.485, 0.96, 0.485, 0.88, { stroke: strokeStrong, sw: sw + 0.2, key: "entry-arrow" })}
          {Seg(0.46, 0.91, 0.485, 0.88, { stroke: strokeStrong, sw: sw + 0.2, key: "ea-l" })}
          {Seg(0.51, 0.91, 0.485, 0.88, { stroke: strokeStrong, sw: sw + 0.2, key: "ea-r" })}
        </g>
      );

    case "office":
      return (
        <g>
          {Rect(PERIM.u1, PERIM.v1, PERIM.u2, PERIM.v2, { key: "perim" })}
          {Seg(PERIM.u1, 0.50, PERIM.u2, 0.50, { stroke: strokeStrong, sw: sw, key: "h-walk" })}
          {Seg(0.50, PERIM.v1, 0.50, PERIM.v2, { stroke: strokeStrong, sw: sw, key: "v-walk" })}
          {(() => {
            const cells: React.ReactElement[] = [];
            const us = [0.13, 0.36, 0.59, 0.82];
            const vs = [0.13, 0.36, 0.59, 0.82];
            for (let i = 0; i < 3; i++) {
              for (let j = 0; j < 3; j++) {
                cells.push(Rect(us[i]!, vs[j]!, us[i + 1]!, vs[j + 1]!, { key: `c-${i}-${j}` }));
                const u = (us[i]! + us[i + 1]!) / 2;
                const v = (vs[j]! + vs[j + 1]!) / 2;
                cells.push(Dot(u, v, 1.2, `d-${i}-${j}`));
              }
            }
            return cells;
          })()}
        </g>
      );

    case "location":
      return (
        <g>
          {Rect(PERIM.u1, PERIM.v1, PERIM.u2, PERIM.v2, { key: "perim", sw: sw + 0.2 })}
          {Seg(PERIM.u1, 0.50, PERIM.u2, 0.50, { stroke: strokeStrong, sw: sw + 0.2, dashed: true, key: "h-corr" })}
          {Seg(0.50, PERIM.v1, 0.50, PERIM.v2, { stroke: strokeStrong, sw: sw + 0.2, dashed: true, key: "v-corr" })}
          {Rect(0.16, 0.16, 0.46, 0.45, { fill: fillSubtle, key: "rack-bay" })}
          {[0.19, 0.24, 0.29, 0.34, 0.39, 0.44].map((u, k) =>
            Seg(u, 0.18, u, 0.43, { stroke, sw: sw - 0.1, key: `rack-${k}` }),
          )}
          {Rect(0.55, 0.16, 0.84, 0.45, { fill: fillSubtle, key: "mon-room" })}
          {Seg(0.62, 0.40, 0.62, 0.22, { stroke: strokeStrong, sw, key: "u-l" })}
          {Seg(0.62, 0.22, 0.78, 0.22, { stroke: strokeStrong, sw, key: "u-t" })}
          {Seg(0.78, 0.22, 0.78, 0.40, { stroke: strokeStrong, sw, key: "u-r" })}
          {Dot(0.70, 0.42, 1.6, "u-chair")}
          {Rect(0.16, 0.55, 0.46, 0.84, { dashed: true, key: "ops" })}
          {Rect(0.55, 0.55, 0.84, 0.84, { fill: fillSubtle, key: "ctl" })}
          {(() => {
            const [cx, cy] = placeOnFloor(floor, 0.69, 0.70);
            return (
              <g key="podium">
                <ellipse
                  cx={cx} cy={cy} rx={18} ry={9}
                  fill="rgba(0, 220, 255, 0.16)"
                  stroke={strokeStrong}
                  strokeWidth={sw + 0.1}
                />
                <ellipse
                  cx={cx} cy={cy} rx={11} ry={5.5}
                  fill="none"
                  stroke={stroke}
                  strokeWidth={sw - 0.1}
                  strokeDasharray="2 3"
                />
              </g>
            );
          })()}
        </g>
      );

    case "meeting":
      return (
        <g>
          {Rect(PERIM.u1, PERIM.v1, PERIM.u2, PERIM.v2, { key: "perim" })}
          {Rect(0.16, 0.16, 0.40, 0.84, { key: "bay-1" })}
          {Rect(0.40, 0.16, 0.62, 0.84, { key: "bay-2" })}
          {Rect(0.62, 0.16, 0.84, 0.84, { key: "bay-3" })}
          {[0.28, 0.51, 0.73].map((u, k) => {
            const [cx, cy] = placeOnFloor(floor, u, 0.50);
            return (
              <ellipse
                key={`tbl-${k}`}
                cx={cx} cy={cy}
                rx={14} ry={6}
                fill={fillSubtle}
                stroke={strokeStrong}
                strokeWidth={sw}
              />
            );
          })}
          {[0.28, 0.51, 0.73].flatMap((u, k) => {
            const dots: React.ReactElement[] = [];
            for (let i = 0; i < 4; i++) {
              const local_u = u - 0.045 + (i * 0.03);
              dots.push(Dot(local_u, 0.43, 1.0, `chN-${k}-${i}`));
            }
            for (let i = 0; i < 4; i++) {
              const local_u = u - 0.045 + (i * 0.03);
              dots.push(Dot(local_u, 0.57, 1.0, `chS-${k}-${i}`));
            }
            return dots;
          })}
          {Seg(0.28, 0.16, 0.28, 0.20, { sw: sw + 0.2, key: "d-1" })}
          {Seg(0.51, 0.16, 0.51, 0.20, { sw: sw + 0.2, key: "d-2" })}
          {Seg(0.73, 0.16, 0.73, 0.20, { sw: sw + 0.2, key: "d-3" })}
        </g>
      );

    case "exec":
      return (
        <g>
          {Rect(PERIM.u1, PERIM.v1, PERIM.u2, PERIM.v2, { key: "perim" })}
          {Rect(0.14, 0.14, 0.55, 0.84, { key: "suite" })}
          {Rect(0.22, 0.30, 0.42, 0.40, { fill: fillSubtle, key: "exec-desk" })}
          {Curve(0.18, 0.66, 0.30, 0.78, 0.46, 0.66, { sw: sw + 0.4, key: "sofa-arc" })}
          {Curve(0.20, 0.70, 0.30, 0.80, 0.44, 0.70, { sw: sw + 0.1, key: "sofa-arc-inner" })}
          {Dot(0.16, 0.55, 1.6, "plant-1")}
          {Dot(0.50, 0.55, 1.6, "plant-2")}
          {Dot(0.50, 0.80, 1.6, "plant-3")}
          {Rect(0.58, 0.55, 0.86, 0.84, { key: "side-office" })}
          {Rect(0.65, 0.62, 0.78, 0.68, { fill: fillSubtle, key: "side-desk" })}
          {Rect(0.58, 0.14, 0.86, 0.50, { dashed: true, key: "obs" })}
          {[0.62, 0.66, 0.70, 0.74, 0.78, 0.82].map((u, k) =>
            Seg(u, 0.18, u + 0.04, 0.30, { stroke, sw: sw - 0.2, key: `hatch-${k}` }),
          )}
          {Dot(0.72, 0.40, 1.6, "obj-1")}
          {Dot(0.82, 0.30, 1.6, "obj-2")}
        </g>
      );
  }
}

// ── Patrol indicator ─────────────────────────────────────────────────────

interface PatrolDotProps {
  x: number;
  y: number;
  variant: "green" | "red";
}

function PatrolDot({ x, y, variant }: PatrolDotProps): React.ReactElement {
  const isRed = variant === "red";
  const r = isRed ? 10 : 9;
  const ringR = isRed ? 24 : 16;
  const fill = isRed ? "#ff4d6d" : "#00d97e";
  const glow = isRed ? "rgba(255, 77, 109, 0.6)" : "rgba(0, 217, 126, 0.55)";
  return (
    <g className={styles.patrolGroup}>
      <circle
        cx={x} cy={y}
        r={ringR}
        fill="none"
        stroke={glow}
        strokeWidth={isRed ? 1.8 : 1.2}
        className={isRed ? styles.breathRingRed : styles.breathRing}
      />
      <circle
        cx={x} cy={y} r={r} fill={fill}
        className={isRed ? styles.breathDotRed : styles.breathDot}
        style={{ filter: `drop-shadow(0 0 ${r}px ${glow})` }}
      />
      {isRed && (
        <circle cx={x} cy={y} r={3} fill="rgba(255,255,255,0.92)" />
      )}
    </g>
  );
}
